import React, { CSSProperties, useMemo } from "react";
import style from "./opacity-slider.module.css";
import { generateOpacityGradient } from "utils/color-utils";
import cn from "classnames";

interface OpacitySliderProps {
  /**
   * The current opacity value (0 to 100).
   */
  value: number;
  /**
   * Callback when the value changes.
   */
  onChange: (newValue: number) => void;
  /**
   * Callback when the user finishes editing (mouse up)
   */
  onFinish: (finalValue: number) => void;
  /**
   * The base color for the gradient (e.g., "#3B82F6").
   */
  color: string;
  /**
   * Optional label text. Defaults to "Opacity".
   */
  label?: string;
  /**
   * Optional disabled state.
   */
  disabled?: boolean;
}

export const OpacitySlider: React.FC<OpacitySliderProps> = ({
  value,
  onChange,
  onFinish,
  color,
  label = "Opacity",
  disabled = false,
}) => {
  // Memoize the gradient string to avoid recalculating on every render unless color changes
  const backgroundGradient = useMemo(() => generateOpacityGradient(color), [color]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(Number(e.target.value));
  };

  return (
    <div className={cn(style.root, disabled && style.disabled)} style={{ "--backColor": color } as CSSProperties}>
      {/* Header: Label and Percent Value */}
      <div className={style.header}>
        <label htmlFor="opacity-slider-input">{label}</label>
        <span className={style.value}>{value}%</span>
      </div>

      {/* Slider Container */}
      <div className={style.trackContainer}>
        {/* Layer 1: Checkerboard Pattern (Transparency Indicator) */}
        <div className={style.checkerboardLayer} />

        {/* Layer 2: Color Gradient (Transparent -> Opaque) */}
        <div className={style.gradientLayer} style={{ background: backgroundGradient }} />

        {/* Layer 3: The Interactive Range Input */}
        <input
          id="opacity-slider-input"
          type="range"
          min="10"
          max="100"
          step="1"
          value={value}
          onChange={handleChange}
          onSelect={(e) => onFinish(Number(e.currentTarget.value))}
          disabled={disabled}
          className={style.input}
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={value}
        />
      </div>
    </div>
  );
};
